import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { TempAndPersistedAvatarState } from '../../components/v2BaseComponents/ImagePicker/TempImagePicker';
import { useClearInMemoryAvatarCache } from '../useAvatar';
import { appAPI } from '../../utils/api';
import { getMedicalProfileIdCardsCacheKey, onIdCardUpdate } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useMedicalProfileCardsCards', {
    errorLoadingProfileCards: 'Error loading profile cards',
    createInsuranceCardSuccess: 'Insurance card created successfully',
    createInsuranceCardError: 'Error occurred while creating insurance card',
    deleteInsuranceCardSuccess: 'Insurance card deleted successfully',
    deleteInsuranceCardError: 'Error occurred while deleting insurance card',
    updateInsuranceCardSuccess: 'Insurance card updated successfully',
    updateInsuranceCardError: 'Error occurred while updating insurance card',
    createIdCardSuccess: 'ID card created successfully',
    createIdCardError: 'Error occurred while creating ID card',
    deleteIdCardSuccess: 'ID card deleted successfully',
    deleteIdCardError: 'Error occurred while deleting ID card',
    updateIdCardSuccess: 'ID card updated successfully',
    updateIdCardError: 'Error occurred while updating ID card',
});

export type UseMedicalProfileCardsCardsArgs = {
    medicalProfileId: string;
    onCreateCardSuccess?: () => void;
    onDeleteCardSuccess?: () => void;
    onUpdateCardSuccess?: () => void;
};

export type UpdateContactDetailsArgs = {
    contactDetailsId: string;
    contactDetails: APITypes.medicalProfileService.UpdateContactDetailsBody;
};

export type CreateEmergencyContactArgs = {
    emergencyContactDetails: APITypes.medicalProfileService.CreateEmergencyContactDetailsBody;
    medicalProfileId: string;
};

export type DeleteEmergencyContactArgs = {
    medicalProfileId: string;
    emergencyContactId: string;
};

export type ProfileAvatarUpdateArgs = {
    avatarState: TempAndPersistedAvatarState;
};

export type UpsertCardArgs = {
    cardId: string;
    update:
        | APITypes.medicalProfileService.UpsertMedicalProfileInsuranceCardRequest
        | APITypes.medicalProfileService.UpsertMedicalProfileIdCardRequest;
};

export type UseMedicalProfileCardsCardsReturnValue = {
    isLoading: boolean;
    cards: APITypes.medicalProfileService.GetMedicalProfilesIdCardsResponse;
    createInsuranceCard: (args: APITypes.medicalProfileService.UpsertMedicalProfileInsuranceCardRequest) => void;
    isCreatingInsuranceCard: boolean;
    deleteInsuranceCard: (insuranceCardId: string) => void;
    isDeletingInsuranceCard: boolean;
    updateInsuranceCard: (args: UpsertCardArgs) => void;
    isUpdatingInsuranceCard: boolean;
    createIdCard: (args: APITypes.medicalProfileService.UpsertMedicalProfileIdCardRequest) => void;
    isCreatingIdCard: boolean;
    deleteIdCard: (idCardId: string) => void;
    isDeletingIdCard: boolean;
    updateIdCard: (args: UpsertCardArgs) => void;
    isUpdatingIdCard: boolean;
};

export default function useMedicalProfileCardsCards({
    medicalProfileId,
    onCreateCardSuccess,
    onDeleteCardSuccess,
    onUpdateCardSuccess,
}: UseMedicalProfileCardsCardsArgs): UseMedicalProfileCardsCardsReturnValue {
    const notify = useNotify();
    const clearInMemoryAvatarCache = useClearInMemoryAvatarCache();
    const resetCardCache = onIdCardUpdate.resetCache({ medicalProfileId });
    const cardsQuery = useQuery({
        queryKey: getMedicalProfileIdCardsCacheKey(medicalProfileId),
        queryFn: async () => {
            return await appAPI.get(urls.api.medicalProfiles.cards.cards(medicalProfileId));
        },
    });
    useQueryErrorNotify(cardsQuery, intl('errorLoadingProfileCards'));

    const createInsuranceCardMutation = useMutation({
        mutationFn: async (args: APITypes.medicalProfileService.UpsertMedicalProfileInsuranceCardRequest) => {
            return await appAPI.post(urls.api.medicalProfiles.cards.insuranceCards(medicalProfileId), {
                body: args,
            });
        },
        onError: () => {
            notify('error', intl('createInsuranceCardError'));
        },
        onSuccess: () => {
            notify('success', intl('createInsuranceCardSuccess'));
            onCreateCardSuccess?.();
            resetCardCache();
        },
    });

    const createIdCardMutation = useMutation({
        mutationFn: async (args: APITypes.medicalProfileService.UpsertMedicalProfileIdCardRequest) => {
            return await appAPI.post(urls.api.medicalProfiles.cards.idCards(medicalProfileId), {
                body: args,
            });
        },
        onError: () => {
            notify('error', intl('createIdCardError'));
        },
        onSuccess: () => {
            notify('success', intl('createIdCardSuccess'));
            onCreateCardSuccess?.();
            resetCardCache();
        },
    });

    const deleteInsuranceCardMutation = useMutation({
        mutationFn: async (insuranceCardId: string) => {
            return await appAPI.del(urls.api.medicalProfiles.cards.insuranceCard(medicalProfileId, insuranceCardId));
        },
        onError: () => {
            notify('error', intl('deleteInsuranceCardError'));
        },
        onSuccess: () => {
            notify('success', intl('deleteInsuranceCardSuccess'));
            onDeleteCardSuccess?.();
            resetCardCache();
        },
    });

    const deleteIdCardMutation = useMutation({
        mutationFn: async (idCardId: string) => {
            return await appAPI.del(urls.api.medicalProfiles.cards.idCard(medicalProfileId, idCardId));
        },
        onError: () => {
            notify('error', intl('deleteIdCardError'));
        },
        onSuccess: () => {
            notify('success', intl('deleteIdCardSuccess'));
            onDeleteCardSuccess?.();
            resetCardCache();
        },
    });

    const updateInsuranceCardMutation = useMutation({
        mutationFn: async (args: UpsertCardArgs) => {
            return await appAPI.put(urls.api.medicalProfiles.cards.insuranceCard(medicalProfileId, args.cardId), {
                body: args.update,
            });
        },
        onError: () => {
            notify('error', intl('updateInsuranceCardError'));
        },
        onSuccess: (_data, args) => {
            notify('success', intl('updateInsuranceCardSuccess'));
            onUpdateCardSuccess?.();
            clearInMemoryAvatarCache(urls.api.medicalProfiles.cards.insuranceCardImage(medicalProfileId, args.cardId));
            resetCardCache();
        },
    });

    const updateIdCardMutation = useMutation({
        mutationFn: async (args: UpsertCardArgs) => {
            return await appAPI.put(urls.api.medicalProfiles.cards.idCard(medicalProfileId, args.cardId), {
                body: args.update,
            });
        },
        onError: () => {
            notify('error', intl('updateIdCardError'));
        },
        onSuccess: (_data, args) => {
            notify('success', intl('updateIdCardSuccess'));
            onUpdateCardSuccess?.();
            clearInMemoryAvatarCache(urls.api.medicalProfiles.cards.insuranceCardImage(medicalProfileId, args.cardId));
            resetCardCache();
        },
    });

    return {
        isLoading: cardsQuery.isLoading,
        cards: cardsQuery.data || {
            insuranceCards: [],
            idCards: [],
        },
        createInsuranceCard: createInsuranceCardMutation.mutate,
        isCreatingInsuranceCard: createInsuranceCardMutation.isPending,
        deleteInsuranceCard: deleteInsuranceCardMutation.mutate,
        isDeletingInsuranceCard: deleteInsuranceCardMutation.isPending,
        updateInsuranceCard: updateInsuranceCardMutation.mutate,
        isUpdatingInsuranceCard: updateInsuranceCardMutation.isPending,
        createIdCard: createIdCardMutation.mutate,
        isCreatingIdCard: createIdCardMutation.isPending,
        deleteIdCard: deleteIdCardMutation.mutate,
        isDeletingIdCard: deleteIdCardMutation.isPending,
        updateIdCard: updateIdCardMutation.mutate,
        isUpdatingIdCard: updateIdCardMutation.isPending,
    };
}
